import { untracked } from '@angular/core';
import { ColDef, GridOptions, ICellRendererParams, RowGroupingDisplayType, themeBalham, ValueFormatterParams } from 'ag-grid-enterprise';
import { DataCurrencyNegativeDisplay, DataCurrencySign, DataNumberFormat } from 'src/app/services/api.services';
import { DataDisplayOptions } from '../../dataModel';
import { DataCurrencyIndicators, DataFormatter } from '../../formatting/DataFormatter';
import { VisualComponent } from '../base/Visual.component';
import { SerializationOptionsFactory, VisualDataSerializer } from '../VisualDataSerializer';
import { VisualField } from '../VisualField';
import { TableField } from './table/TableVisual.component';
import { isNullOrWhitespace } from 'src/app/shared/utils';

const baseGridOptions: GridOptions = {
  animateRows: false,
  loading: false,
  suppressNoRowsOverlay: true,
  suppressDragLeaveHidesColumns: true,
  enableBrowserTooltips: true,
  popupParent: document.body,
};

const baseTheme = themeBalham.withParams({
  fontFamily: 'Lato, sans-serif',
  fontSize: '12px',

  textColor: '#2e2e2e',
  cellTextColor: '#2e2e2e',
  menuTextColor: '#2e2e2e',
  subtleTextColor: '#d6d5d5',
  
  backgroundColor: '#ffffff',
  borderWidth: '0',
  
  headerBackgroundColor: '#dfdfdf',
  headerTextColor: '#2e2e2e',
  headerFontWeight: '400',
  headerRowBorder: '2px solid #F29100',
  
  rowBorder: 'thin solid #e6e6e6',
  rowHoverColor: '#e5e5e5',
  oddRowBackgroundColor: '#ffffff',

  //selectedRowBackgroundColor: '#d6d5d5',

  rangeSelectionBorderColor: '#0091ea',
  rangeSelectionBorderStyle: 'solid',

  columnBorder: 'none',
});

export const MIN_COLUMN_WIDTH = 70;
export const MAX_COLUMN_WIDTH = 350;

export function getDefaultColumnWidth(vf: VisualField) {
  let [dataType, display] = [vf.dataType, vf.dataDisplay];

  if (dataType.isDateOrDateTime) {
    return 110;
  }

  if (dataType.isTimeOnly) {
    return 90;
  }

  if (dataType.isNumeric) {
    return 90;
  }

  return null;
}

export const data = {
  ag: {
    options: <GridOptions>{
      ...baseGridOptions,

      animateRows: false,

      rowSelection: {
        mode:                 'multiRow',
        checkboxes:           false,
        headerCheckbox:       false,
        enableClickSelection: true,
      },

      cellSelection: true,
      
      rowBuffer: 20,
      cacheBlockSize: 500,
      serverSideInitialRowCount: 10,
      cacheOverflowSize: 10,

      suppressServerSideFullWidthLoadingRow: true,
      
      defaultColDef: {
        loadingCellRendererParams: { deferRender: true },
        minWidth: MIN_COLUMN_WIDTH,
      },
      
      skipHeaderOnAutoSize: false,
      suppressAutoSize: false,
      
      navigateToNextHeader: x => {
        return null;
      },

      navigateToNextCell: x => {
        if (x.nextCellPosition.rowIndex < 0)
          return x.previousCellPosition;

        return x.nextCellPosition;
      },
    },

    theme: baseTheme.withParams({
      headerHeight: '36px',
      rowHeight: '36px',

      headerColumnResizeHandleWidth: '1px',
      headerColumnResizeHandleHeight: '14px',
      headerColumnResizeHandleColor: '#ababab',

      rowHoverColor: '#E4F2FD',
    }),
    
    columnDef: <ColDef>{
      valueFormatter: CellFormatter,
      cellRenderer: CellRenderer,
      cellRendererParams: { deferRender: true },

      resizable: true,
      sortable:  true,
    },
  },
};

export const grandTotals = {
  ag: {
    options: <GridOptions>{
      ...baseGridOptions,

      animateRows: false,

      headerHeight: 0,
      scrollbarWidth: 6,

      alwaysShowVerticalScroll: true,

      cellSelection: false,

      suppressCellFocus: true,
      suppressServerSideFullWidthLoadingRow: true,
      
      defaultColDef: {
        loadingCellRendererParams: { deferRender: true },
        minWidth: MIN_COLUMN_WIDTH,
      }
    },

    theme: baseTheme.withParams({
      headerHeight: '0px',
      headerRowBorder: 'none',
  
      rowHeight: '36px',
      rowBorder: 'none',
      rowHoverColor: '#ffffff',
      oddRowBackgroundColor: '#ffffff',
      selectedRowBackgroundColor: '#ffffff',
    }),

    columnDef: <ColDef>{
      valueFormatter: CellFormatter,
      cellRenderer: CellRenderer,
      cellRendererParams: { deferRender: true },

      suppressMovable:   true,
      sortable:          false,
      suppressNavigable: true,
    },
  }
};


export interface AgGridCellData {
  readonly context?: any;
  readonly value: any;
  readonly leftText: string;
  readonly rightText: string;
  readonly valueText: string;
}
 
export namespace AgGridCellData {
  export function create(visual: VisualComponent, value: any, vf: VisualField, context?: any): AgGridCellData {
    let leftText = '';
    let rightText = '';
    let valueText = '';

    let formatter = visual.formatter();

    if (!vf) {
      rightText = formatter.dataFormatter.formatForDisplay(value);
      valueText = rightText;
    }
    else {
      let isCurrency =
        vf.dataType.isNumeric &&
        vf.dataDisplay?.numberFormat === DataNumberFormat.Currency;

      if (isCurrency) {
        let currencyDisplay = vf.dataDisplay.currency ?? DataDisplayOptions.defaults.currency;

        let leftTextBuilder = [];

        let ignoreCurrency = false; // for the right text, as it will be on the left
        let absoluteValue = false;

        if (currencyDisplay.negativeDisplay == DataCurrencyNegativeDisplay.WithSign) {
          absoluteValue = true;

          let plusMinusSign =
            vf.dataType.isNumeric && DataFormatter.isNegative(value)
              ? '-'
              : '';

          leftTextBuilder.push(plusMinusSign);
        }

        if (isCurrency && currencyDisplay.sign !== DataCurrencySign.None) {
          ignoreCurrency = true;

          let currency = visual.currency();

          let currencyIndicators = DataCurrencyIndicators.get(visual.locale().id, currency.currencyCode);
          let indicator =
            currencyIndicators.get(currencyDisplay.sign)
            ?? currencyIndicators.get(DataCurrencySign.Code)
            ?? '???';

          leftTextBuilder.push(indicator);
        }

        leftText = leftTextBuilder.join('');
        rightText = formatter.getValueDisplay(vf, value, { ignoreCurrency, absoluteValue });
        valueText = formatter.getValueDisplay(vf, value);
      }
      else {
        rightText = formatter.getValueDisplay(vf, value);
        valueText = rightText;
      }
    }

    return {
      context,
      value,
      leftText,
      rightText: rightText ?? "-",
      valueText: valueText ?? "-"
    };
  }
}

function CellFormatter(params: ValueFormatterParams<any, AgGridCellData, TableField>) {
  if (typeof params.value !== 'object')
    return params.value;

  return params.value.valueText;
}

function CellRenderer(params: ICellRendererParams<any, AgGridCellData, TableField>) {
  return untracked(() => {
    if (typeof params.value !== 'object')
      return params.value;

    let spanL: HTMLElement, spanR: HTMLElement, div: HTMLElement;
    
    div = document.createElement('div');
    div.classList.add('pbir-ag-cell');
    div.title = params.valueFormatted;

    if (isNullOrWhitespace(params.value?.value)) {
      div.classList.add('c-grey-10');
    }

    if (params.value.leftText) {
      spanL = document.createElement('span');
      spanL.classList.add('pbir-ag-cell-left');
      spanL.innerText = params.value.leftText;
      div.appendChild(spanL);
    }
    else {
      div.classList.add('pbir-ag-cell-one-value');
    }

    spanR = document.createElement('span');
    spanR.classList.add('pbir-ag-cell-right');
    spanR.innerText = params.value.rightText;
    div.appendChild(spanR);

    return div;
  });
}


export interface VisualColumnState {
  readonly field: string;
  readonly width?: number;
  readonly flex?: number;
  readonly userResized?: boolean;
  readonly totalsHidden?: boolean;
}


export class TableLayout {
  constructor(
    readonly id: string,
    readonly title: string,
    readonly config: {
      headerHeight: number;
      rowHeight: number;
      grandTotalRowHeight: number;
      fontSize: string;

      sortIndicatorFontSize: string;
      sortIndicatorSize: number;

      rowGroupIndentSize: number;
    }) { }

  readonly headerHeightPx = `${this.config.headerHeight}px`;
  readonly rowHeightPx = `${this.config.rowHeight}px`;
  readonly grandTotalRowHeightPx = `${this.config.grandTotalRowHeight}px`;

  readonly fontSize = this.config.fontSize;

  readonly sortIndicatorFontSize = this.config.sortIndicatorFontSize;
  readonly sortIndicatorSizePx = `${this.config.sortIndicatorSize}px`;

  readonly rowGroupIndentSize = this.config.rowGroupIndentSize;
}

export namespace TableLayout {
  export const standard = new TableLayout('standard', 'Default', {
    headerHeight: 36,
    rowHeight: 36,
    grandTotalRowHeight: 26,
    fontSize: '12px',

    sortIndicatorFontSize: '11px',
    sortIndicatorSize: 15,

    rowGroupIndentSize: 22,
  });
  
  export const small = new TableLayout('small', 'Small', {
    headerHeight: 36,
    rowHeight: 26,
    grandTotalRowHeight: 26,
    fontSize: '12px',

    sortIndicatorFontSize: '11px',
    sortIndicatorSize: 15,

    rowGroupIndentSize: 20,
  });

  export const compact = new TableLayout('compact', 'Compact', {
    headerHeight: 24,
    rowHeight: 24,
    grandTotalRowHeight: 24,
    fontSize: '10px',

    sortIndicatorFontSize: '9px',
    sortIndicatorSize: 12,

    rowGroupIndentSize: 16,
  });

  export const list = [
    standard,
    small,
    // compact <- not needed
  ];

  export const serializationOptions: SerializationOptionsFactory<TableLayout, string> = (s: VisualDataSerializer) => ({
    defaultValue: TableLayout.standard,
    read:  x => list.find(s => s.id == x) ?? standard,
    write: x => x?.id,
  });
}

export class PivotColumnDisplay {
  constructor(
    readonly id: string,
    readonly title: string,
    readonly config: {
      display: RowGroupingDisplayType,
      disableIndent: boolean,
    }) { }
    
  readonly display = this.config.display;
  readonly disableIndent = this.config.disableIndent;
}

export namespace PivotColumnDisplay {
  export const single = new PivotColumnDisplay('single', 'Single Column', {
    display: 'singleColumn',
    disableIndent: false,
  });
  
  export const multi = new PivotColumnDisplay('multi', 'Multiple Columns', {
    display: 'multipleColumns',
    disableIndent: true,
  });

  export const list = [single, multi];

  export const serializationOptions: SerializationOptionsFactory<PivotColumnDisplay, string> = (s: VisualDataSerializer) => ({
    defaultValue: PivotColumnDisplay.single,
    read:  x => list.find(s => s.id == x) ?? single,
    write: x => x?.id,
  });
}
